import { ElementType, FunctionComponent, memo, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link, useSearchParams } from 'react-router-dom';
import sprite from '../../../assets/icons/sprite.svg';
import { PostedIn } from '../../../components';
import { useAppSelector, useSearchByTags, useSearchFeed } from '../../hooks';
import { layoutPath } from '../../utils';
import { Badge } from '../Badge';
import { FeedCardLoader, FeedCardProps } from '../FeedCard';

import classes from './SearchFeedCommon.module.scss';

interface SearchFeedCommonProps {
  CardComponent: ElementType<FeedCardProps>;
}

export const SearchFeedCommon: FunctionComponent<SearchFeedCommonProps> = memo(
  ({ CardComponent }) => {
    const { t } = useTranslation();

    const { getSearchFeedItems, loadMoreSearchFeedItems } = useSearchFeed();

    const [searchParams] = useSearchParams();

    const { tagsSearchParams, tagsToSearchBy, searchByTagIdRemoval } = useSearchByTags();

    const query = searchParams.get('qs') ?? '';

    const { isFetched, searchFeed, hasNextPage, totalItems } = useAppSelector(
      ({ searchFeed }) => searchFeed
    );

    const hasCards = useMemo(() => Boolean(searchFeed.length), [searchFeed.length]);

    const renderTags = useMemo(() => {
      const items = tagsToSearchBy.filter(({ id }) =>
        tagsSearchParams.split('_').includes(`${id}`)
      );

      if (!items.length) {
        return null;
      }

      return (
        <div className={classes['search__tags']}>
          {items.map(({ id, name }) => (
            <Badge key={id} value={name} onRemove={() => searchByTagIdRemoval(id)} />
          ))}
        </div>
      );
    }, [searchByTagIdRemoval, tagsSearchParams, tagsToSearchBy]);

    useEffect(() => {
      getSearchFeedItems();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, tagsSearchParams]);

    const renderFeed = useMemo(() => {
      if (!hasCards) {
        return (
          <div className={classes['search__no-result']}>
            <svg className={classes['search__no-result-icon']}>
              <use href={`${sprite}#search-off`} />
            </svg>
            <div className={classes['search__no-result-text']}>
              <Trans i18nKey={'search.nothing-found'}>
                <Link className={classes['search__no-result-link']} to={layoutPath('/')}></Link>
              </Trans>
            </div>
          </div>
        );
      }

      return (
        <InfiniteScroll
          className={classes['search__feed']}
          next={loadMoreSearchFeedItems}
          hasMore={hasNextPage}
          loader={<FeedCardLoader />}
          dataLength={searchFeed.length}
        >
          <div className={classes['search__feed-cards']}>
            {searchFeed.map((card, index, searchFeedArray) => {
              return (
                <div key={`story-card-item-${card.id}`}>
                  {card.story.id !== searchFeedArray[index - 1]?.story.id && (
                    <PostedIn story={card.story} />
                  )}
                  <CardComponent card={card} />
                </div>
              );
            })}
          </div>
        </InfiniteScroll>
      );
    }, [CardComponent, hasCards, hasNextPage, loadMoreSearchFeedItems, searchFeed]);

    if (!isFetched) {
      return <FeedCardLoader />;
    }

    return (
      <>
        {query && (
          <h1 className={classes['search__title']}>
            {t('search.results-for')}&nbsp;
            <span className={classes['search__title-query']}>{query}</span>
          </h1>
        )}
        <div className={classes['search__count']}>
          {t('search.results-found', { count: totalItems })}
        </div>
        {renderTags}
        {renderFeed}
      </>
    );
  }
);
